import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  TrendingUp, 
  RefreshCw,
  Calendar,
  Clock,
  Zap,
  Wallet
} from 'lucide-react';

const DUBAI_TIMEZONE = 'Asia/Dubai';
const INDIA_TIMEZONE = 'Asia/Kolkata';
const ROI_CREDIT_TIME_INDIA_24 = '01:30'; // 1:30 AM IST next day (after Dubai midnight)

const ROIIncome = () => {
  const CACHE_TTL_MS = 30000;
  const [roiIncomes, setRoiIncomes] = useState([]);
  const [upcomingROI, setUpcomingROI] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [walletBalance, setWalletBalance] = useState(0);
  const [stats, setStats] = useState({
    totalROI: 0,
    todayROI: 0,
    blockedROI: 0,
    activeROI: 0,
    pendingROI: 0,
    upcomingTotal: 0,
    activeSchedules: 0
  });

  const userDbKey = localStorage.getItem('dbKey');
  const userId = localStorage.getItem('userId');
  const userSponsorId = localStorage.getItem('userSponsorId');
  const userRegistrationDate = localStorage.getItem('registrationDate');

  useEffect(() => {
    if (userId) {
      fetchROIData();
    }
  }, [userId]);

  const getDateKeyInTimezone = (date, timezone = DUBAI_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const getWeekdayInTimezone = (date, timezone = DUBAI_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short'
    }).format(date);
  };

  const getRoiCreditTimeDubai = () => {
    const now = new Date();
    // Dubai credits happen at 12:00 AM Dubai time
    return now.toLocaleTimeString('en-US', {
      timeZone: DUBAI_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(/\s?(AM|PM)/, ' $1');
  };

  const getRoiCreditTimeIndia = () => {
    const now = new Date();
    // Convert Dubai midnight to India time
    const dateParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: DUBAI_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);
    
    const year = Number(dateParts.find(p => p.type === 'year')?.value);
    const month = Number(dateParts.find(p => p.type === 'month')?.value);
    const day = Number(dateParts.find(p => p.type === 'day')?.value);
    
    // Dubai midnight UTC+4
    const dubaiMidnightUTC = Date.UTC(year, month - 1, day, 0 - 4, 0, 0);
    const indiaTime = new Date(dubaiMidnightUTC);
    
    return indiaTime.toLocaleTimeString('en-US', {
      timeZone: INDIA_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(/\s?(AM|PM)/, ' $1');
  };

  // Helper: Check if a day is a working day (Dubai timezone)
  const isWorkingDay = (workingDaysPattern = 'Mon-Fri', date = new Date()) => {
    const dayName = getWeekdayInTimezone(date, DUBAI_TIMEZONE);
    const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(dayName);
    const isSaturday = dayName === 'Sat';
    
    if (workingDaysPattern === 'Mon-Fri') {
      return isWeekday;
    } else if (workingDaysPattern === 'Mon-Sat') {
      return isWeekday || isSaturday;
    } else if (workingDaysPattern === 'All') {
      return true;
    }
    
    return isWeekday;
  };

  // Helper: Calculate next working day
  const getNextWorkingDay = (workingDaysPattern = 'Mon-Fri', fromDate = new Date()) => {
    const nextDate = new Date(fromDate);
    nextDate.setDate(nextDate.getDate() + 1);
    
    while (!isWorkingDay(workingDaysPattern, nextDate)) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    
    return nextDate;
  };

  const parseDateKeyToDubaiRunDate = (dateKey) => {
    const [year, month, day] = (dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    // Dubai midnight = previous day 20:00 UTC (UTC+4, fixed no DST)
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - (4 * 60 * 60 * 1000));
  };

  // Calculate the correct next ROI date based on last credited date
  const calculateNextROIDate = (schedule) => {
    try {
      const now = new Date();
      const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
      const workingDaysPattern = schedule.workingDays || 'Mon-Fri';
      
      // Primary source: backend-calculated next ROI date key (fixed Dubai logic)
      if (schedule.nextRoiDateKey) {
        const nextFromKey = parseDateKeyToDubaiRunDate(schedule.nextRoiDateKey);
        if (isValidDate(nextFromKey)) {
          return nextFromKey;
        }
      }

      // Secondary source: backend-calculated next ROI date
      if (schedule.nextRoiDate) {
        const nextFromBackend = new Date(schedule.nextRoiDate);
        if (isValidDate(nextFromBackend)) {
          return nextFromBackend;
        }
      }
      
      // Fallback for legacy schedules missing nextRoiDate
      const baseDate = new Date(
        schedule.lastRoiDistribution ||
        schedule.startDate ||
        schedule.createdAt ||
        schedule.date ||
        now
      );
      
      let nextDate = isValidDate(baseDate) ? new Date(baseDate) : new Date(now);
      nextDate.setDate(nextDate.getDate() + 1);
      nextDate.setHours(0, 0, 0, 0);
      
      if (!isWorkingDay(workingDaysPattern, nextDate)) {
        nextDate = getNextWorkingDay(workingDaysPattern, nextDate);
      }
      
      while (nextDate < now) {
        nextDate.setDate(nextDate.getDate() + 1);
        nextDate.setHours(0, 0, 0, 0);
        if (!isWorkingDay(workingDaysPattern, nextDate)) {
          nextDate = getNextWorkingDay(workingDaysPattern, nextDate);
        }
      }
      
      return nextDate;
    } catch (error) {
      console.error('Error calculating next ROI date:', error);
      return new Date();
    }
  };

  const fetchROIData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const cacheKey = `roi_income_cache_${userId || 'guest'}`;
      if (!forceRefresh) {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Date.now() - cached.timestamp < CACHE_TTL_MS) {
            setWalletBalance(cached.walletBalance || 0);
            setUpcomingROI(cached.upcomingROI || []);
            setStats(cached.stats || {
              totalROI: 0,
              todayROI: 0,
              blockedROI: 0,
              activeROI: 0,
              pendingROI: 0,
              upcomingTotal: 0,
              activeSchedules: 0
            });
            setLoading(false);
            return;
          }
        }
      }
      
      const now = new Date();
      const todayStr = getDateKeyInTimezone(now, DUBAI_TIMEZONE);
      
      console.log('Fetching ROI data for user:', userId);
      console.log('Today (Dubai GST):', todayStr);
      
      // ===== 1. Get Wallet Balance =====
      let roiWalletBalance = 0;
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          roiWalletBalance = parseFloat(userData.wallets?.roi) || 0;
          setWalletBalance(roiWalletBalance);
          console.log('ROI Wallet Balance:', roiWalletBalance);
        }
      }
      
      // ===== 2. Fetch UPCOMING ROI from ROISchedule =====
      const roiScheduleRef = ref(database, 'ROISchedule');
      const scheduleSnapshot = await get(roiScheduleRef);
      
      const upcomingIncomes = [];
      let upcomingTotal = 0;
      let activeSchedules = 0;
      let pendingToday = 0;
      
      if (scheduleSnapshot.exists()) {
        const scheduleData = scheduleSnapshot.val();
        
        // Search through all dates in ROISchedule
        Object.entries(scheduleData).forEach(([dateKey, dateSchedules]) => {
          if (!dateSchedules) return;
          
          // Look for schedules belonging to this user
          Object.entries(dateSchedules).forEach(([uid, userSchedules]) => {
            if (uid === userId || uid === userDbKey || uid === userSponsorId) {
              Object.entries(userSchedules).forEach(([scheduleId, schedule]) => {
                if (schedule.status === 'active') {
                  const dailyROIAmount = parseFloat(schedule.dailyROIAmount) || 
                    ((parseFloat(schedule.investmentAmount) || 0) * (parseFloat(schedule.dailyROI) || 0)) / 100;
                  
                  const isBlocked = schedule.roiStatus === 'blocked';
                  const isActive = !isBlocked;
                  
                  if (isActive) {
                    upcomingTotal += dailyROIAmount;
                    activeSchedules++;
                  }
                  
                  // Calculate the CORRECT next ROI date
                  const correctNextDate = calculateNextROIDate(schedule);
                  const nextDateStr = schedule.nextRoiDateKey || getDateKeyInTimezone(correctNextDate, DUBAI_TIMEZONE);
                  
                  // Format next date for display (Dubai time)
                  const formattedNextDate = correctNextDate.toLocaleDateString('en-US', {
                    timeZone: DUBAI_TIMEZONE,
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                  
                  const formattedNextTimeDubai = correctNextDate.toLocaleTimeString('en-US', {
                    timeZone: DUBAI_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  });
                  
                  // Convert to India time for reference
                  const indiaTime = new Date(correctNextDate.toLocaleString('en-US', { timeZone: DUBAI_TIMEZONE }));
                  const formattedNextTimeIndia = indiaTime.toLocaleTimeString('en-US', {
                    timeZone: INDIA_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  });
                  
                  const formattedNextTime = `${formattedNextTimeDubai} Dubai / ${formattedNextTimeIndia} India`;
                  
                  // Calculate if overdue
                  const isOverdue = correctNextDate < now;
                  
                  // Calculate time remaining
                  const timeDiff = correctNextDate - now;
                  const hoursRemaining = Math.floor(timeDiff / (1000 * 60 * 60));
                  const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                  
                  let timeRemainingText = '';
                  if (isOverdue) {
                    timeRemainingText = 'Processing - Will be credited soon';
                  } else if (hoursRemaining < 1) {
                    timeRemainingText = `${minutesRemaining} minutes`;
                  } else if (hoursRemaining < 24) {
                    timeRemainingText = `${hoursRemaining}h ${minutesRemaining}m`;
                  } else {
                    timeRemainingText = `${Math.floor(hoursRemaining / 24)}d ${hoursRemaining % 24}h`;
                  }
                  
                  // Check if it's today
                  if (nextDateStr === todayStr && !isOverdue) {
                    pendingToday += dailyROIAmount;
                  }
                  
                  upcomingIncomes.push({
                    id: `upcoming_${scheduleId}`,
                    scheduleId: scheduleId,
                    date: nextDateStr,
                    displayDate: formattedNextDate,
                    displayTime: formattedNextTime,
                    amount: dailyROIAmount,
                    packageName: schedule.packageName || 'Package',
                    description: `Next ROI: ${schedule.packageName}`,
                    nextRoiDate: correctNextDate.toISOString(),
                    timeRemaining: timeRemainingText,
                    hoursRemaining: hoursRemaining,
                    isOverdue: isOverdue,
                    isToday: nextDateStr === todayStr && !isOverdue,
                    isActive: isActive,
                    isBlocked: isBlocked,
                    type: 'upcoming',
                    lastRoiDate: schedule.lastRoiDistribution || schedule.startDate
                  });
                }
              });
            }
          });
        });
      }
      
      // Sort upcoming by date (soonest first)
      upcomingIncomes.sort((a, b) => {
        return new Date(a.nextRoiDate) - new Date(b.nextRoiDate);
      });
      
      console.log(`Found ${upcomingIncomes.length} upcoming ROI schedules`);
      
      // Calculate total ROI (wallet balance represents credited ROI)
      const totalROI = roiWalletBalance;
      
      setRoiIncomes([]); // No transaction history yet
      setUpcomingROI(upcomingIncomes);
      setStats({
        totalROI: totalROI,
        todayROI: 0, // Can't determine without transaction history
        blockedROI: 0,
        activeROI: totalROI,
        pendingROI: pendingToday,
        upcomingTotal,
        activeSchedules
      });

      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        walletBalance: roiWalletBalance,
        upcomingROI: upcomingIncomes,
        stats: {
          totalROI: totalROI,
          todayROI: 0,
          blockedROI: 0,
          activeROI: totalROI,
          pendingROI: pendingToday,
          upcomingTotal,
          activeSchedules
        }
      }));
      
    } catch (error) {
      console.error('Error fetching ROI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (item) => {
    if (item.type === 'upcoming') {
      if (item.isOverdue) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <Clock className="h-3 w-3 mr-1" />
            Processing
          </span>
        );
      }
      if (item.isToday) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
            <Zap className="h-3 w-3 mr-1" />
            Today
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
          <Calendar className="h-3 w-3 mr-1" />
          Scheduled
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
        <TrendingUp className="h-3 w-3 mr-1" />
        Credited
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">ROI Income</h1>
        <p className="text-gray-400 text-sm mt-1">Your daily ROI earnings (Dubai Time - GST, UTC+4)</p>
        <div className="flex items-center gap-4 mt-1">
          <p className="text-xs text-blue-400">
            🇦🇪 Dubai Credit: {getRoiCreditTimeDubai()} (12:00 AM Dubai)
          </p>
          <p className="text-xs text-yellow-400">
            🇮🇳 India Credit: {getRoiCreditTimeIndia()} (1:30 AM India)
          </p>
        </div>
      </div>

      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 rounded-xl p-6 mb-6 border border-green-500/30">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-green-400" />
              <span className="text-gray-300">ROI Wallet Balance</span>
            </div>
            <div className="text-3xl font-bold text-green-400">
              {formatCurrency(walletBalance)}
            </div>
            <p className="text-gray-400 text-xs mt-2">Total ROI earned to date</p>
          </div>
          <div className="p-4 bg-green-500/10 rounded-full">
            <TrendingUp className="h-8 w-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Active Packages</div>
          <div className="text-lg font-bold text-blue-400">{stats.activeSchedules}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Daily ROI</div>
          <div className="text-lg font-bold text-yellow-400">{formatCurrency(stats.upcomingTotal)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Next 24h</div>
          <div className="text-lg font-bold text-orange-400">{formatCurrency(stats.pendingROI)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Total Earned</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.totalROI)}</div>
        </div>
      </div>

      {/* Toggle between Upcoming and History */}
      <div className="bg-gray-800/30 rounded-xl p-1 mb-4 inline-flex">
        <button
          onClick={() => setShowUpcoming(true)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            showUpcoming 
              ? 'bg-primary text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          ⏳ Next ROI ({upcomingROI.length})
        </button>
        <button
          onClick={() => setShowUpcoming(false)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            !showUpcoming 
              ? 'bg-primary text-white' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          📜 History (0)
        </button>
      </div>

      {/* Refresh Button */}
      <div className="mb-4">
        <button
          onClick={() => fetchROIData(true)}
          className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Data
        </button>
      </div>

      {/* Content */}
      <div className="space-y-3">
        {showUpcoming ? (
          // UPCOMING ROI SECTION
          upcomingROI.length > 0 ? (
            upcomingROI.map((item) => (
              <div 
                key={item.id} 
                className={`rounded-xl p-5 border ${
                  item.isOverdue 
                    ? 'bg-yellow-500/10 border-yellow-500/30' 
                    : 'bg-blue-500/10 border-blue-500/30'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Header with type and status */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                        <Zap className="h-3 w-3 mr-1" />
                        NEXT ROI
                      </span>
                      {getStatusBadge(item)}
                    </div>
                    
                    {/* Package Name */}
                    <h3 className="text-white font-bold text-lg mb-2">{item.packageName}</h3>
                    
                    {/* Next ROI Date */}
                    <div className="bg-gray-900/50 rounded-lg p-3 mb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="text-gray-300 text-sm">Next ROI:</span>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-medium">
                            {item.displayDate}
                          </div>
                          <div className="text-xs text-gray-400">
                            {item.displayTime}
                          </div>
                          <div className={`text-xs ${item.isOverdue ? 'text-yellow-400' : 'text-gray-400'}`}>
                            {item.timeRemaining}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-2xl font-bold text-green-400">
                      {formatCurrency(item.amount)}
                    </div>
                    
                    {/* Info Text */}
                    <p className="text-xs text-gray-400 mt-2">
                      {item.isOverdue 
                        ? 'This ROI is being processed and will be credited to your wallet soon'
                        : 'This amount will be credited to your ROI wallet at the time shown'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
              <div className="text-5xl mb-4">⏳</div>
              <h3 className="text-lg font-semibold text-white mb-2">No Upcoming ROI</h3>
              <p className="text-gray-400 mb-4">You don't have any active ROI schedules</p>
              <button
                onClick={() => window.location.href = '/user-dashboard/activate'}
                className="px-6 py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold"
              >
                Buy Package to Start Earning
              </button>
            </div>
          )
        ) : (
          // CREDITED ROI HISTORY SECTION
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Transaction History</h3>
            <p className="text-gray-400 mb-2">ROI is being credited directly to your wallet</p>
            <p className="text-gray-400">Current balance: {formatCurrency(walletBalance)}</p>
            <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <p className="text-sm text-blue-400">
                ℹ️ Your earnings are safe in your wallet balance. 
                The system is working correctly.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROIIncome;